export const BOOLEAN_ATTRIBUTE_TYPES = Object.freeze([Boolean, String]);

/**
 * Convert a string value to a boolean
 * If the value is "true", it returns true, otherwise false
 * If the value is already a boolean, it is returned as is
 *
 * @param {*} value - value to be converted to boolean
 * @returns boolean value
 */
export function toBoolean(value) {
  return value === "true" || value === true;
}

/**
 * Resolve a presence-based boolean attribute value.
 *
 * Unlike {@link toBoolean} (which is value-based and only treats "true"/true as on),
 * this treats the attribute as present for any value except the explicit falsy ones.
 * An empty string (`<el disabled>`) and any non-falsy string (`disabled="disabled"`)
 * count as present; `undefined`, `null`, `false`, and `"false"` count as absent.
 *
 * Use for attributes whose semantics are "present = on, absent = off" (disabled,
 * readonly, required, etc.). It matches how the registration wrapper normalizes the
 * `disabled` attribute (the only attribute normalized at that boundary today).
 *
 * @param {*} value - value to test for presence
 * @returns {boolean}
 */
export function isPresent(value) {
  return value !== undefined && value !== null && value !== false && value !== "false";
}

/**
 * Whether a written value means "no selection". The DOM v-model directive writes a cleared ref
 * as `""`, so the empty string is reserved alongside null and undefined and cannot name a real
 * selection through the `value` property.
 *
 * @param {*} value
 * @returns {Boolean}
 */
export function isNoSelection(value) {
  return value === undefined || value === null || value === "";
}

/**
 * Build a Vue prop definition for a custom-element boolean attribute.
 *
 * Custom-element attributes arrive as strings, so boolean props must accept both
 * `Boolean` and `String` ({@link toBoolean}/{@link isPresent} resolve them at use).
 * This factory centralises that shared `{ type: [Boolean, String], default }` shape.
 *
 * Omitting the argument defaults to `false`. Passing an explicit `undefined`
 * is preserved as an undefined default — some props rely on that tri-state
 * (e.g. `props.closeable === undefined`), so it must survive the round-trip.
 *
 * @param {Boolean|String|undefined} [defaultValue=false] - the prop default (explicit `undefined` is kept)
 * @param {Object} [extra={}] - extra prop options merged onto the definition
 * @returns {{ type: (BooleanConstructor|StringConstructor)[], default: Boolean|String|undefined }}
 *
 * @example
 * disabled: booleanAttribute(),                    // default false
 * loading: booleanAttribute(true),                 // default true
 * checked: booleanAttribute(undefined),            // default undefined (tri-state)
 * hidden: booleanAttribute(false, { external: true }) // extra prop options
 */
export function booleanAttribute(defaultValue, extra = {}) {
  return {
    type: BOOLEAN_ATTRIBUTE_TYPES,
    default: arguments.length > 0 ? defaultValue : false,
    ...extra,
  };
}

/**
 * Convert a string value to a CSS unit
 * If the value can not be parsed, the value is returned as is
 *
 * @param {String} value - value to be converted to CSS unit
 * @param {String} unit - unit to be used
 * @returns {String}
 *
 * @example
 * toCssUnit("12") // "12rem"
 * toCssUnit("12.5") // "12.5rem"
 * toCssUnit("12.5rem") // "12.5rem"
 * toCssUnit("12px") // "12px"
 * toCssUnit("12", "px") // "12px"
 * toCssUnit("12vh") // "12vh"
 */
export function toCssUnit(value, unit = "rem") {
  const number = Number(value);

  return isNaN(number) ? value : `${number}${unit}`;
}

/**
 * Convert a string value to a number
 * If the value can not be parsed, undefined is returned
 * If the value can not be parsed, but returnValue is true, the value is returned
 *
 * @param {String} value - value to be converted to number
 * @param {Boolean} returnValue - return the value if it can not be parsed
 * @returns {Object|undefined}
 */
export function toNumber(value, defaultValue = undefined) {
  const parsedValue = Number(value);

  if (isNaN(parsedValue)) {
    return defaultValue;
  }

  return parsedValue;
}

/**
 * Convert a string value to a number
 * If the value can not be parsed, undefined is returned
 * If the value can not be parsed, but returnValue is true, the value is returned
 *
 * @param {String} value - value to be converted
 * @param {Boolean} returnValue - return the value if it can not be parsed
 * @returns {Object|undefined}
 */
export function toJSON(value, returnValue = false) {
  try {
    if (typeof value === "string") {
      return JSON.parse(value);
    }
  } catch (err) {
    console.error(err);
  }
  if (returnValue) {
    return value;
  }
}

/**
 * Trim a string value and return undefined if the value is empty
 * If the value is not a string, it is returned as is
 *
 * @param {*} value - value to be trimmed
 * @returns value trimmed or undefined if value is empty
 */
export function trimToUndefined(value) {
  if (typeof value === "string") {
    return value.trim() || undefined;
  }
  return value;
}

/**
 * Trim a string value and return null if the value is empty
 * If the value is not a string, it is returned as is
 *
 * @param {*} value - value to be trimmed
 * @returns value trimmed or null if value is empty
 */
export function trimToNull(value) {
  return trimToUndefined(value) ?? null;
}

/**
 * Trim a string value and return undefined if the value is empty or null
 * Mirror of trimToNull for callers that store "no value" as undefined
 *
 * @param {*} value - value to be trimmed
 * @returns value trimmed, or undefined if value is empty or null
 */
export function emptyToUndefined(value) {
  return trimToUndefined(value) ?? undefined;
}

export function toArray(value) {
  return Array.isArray(value) ? value : [value];
}

/**
 * Convert a scale step to its CSS reference property. Steps are named by their pixel value.
 *
 * @param {Number} px - the scale step
 * @returns string - CSS scale property
 *
 * @example
 * toCssScaleProperty(8) // var(--sp-ref-scale-8)
 * toCssScaleProperty(16) // var(--sp-ref-scale-16)
 * toCssScaleProperty(24) // var(--sp-ref-scale-24)
 */
export function toCssScaleProperty(px) {
  return toCssRefProperty("scale", px);
}

/**
 * Construct a CSS property reference.
 * @param {string} category - category of the CSS property
 * @param {*} value - value of the CSS property
 * @returns string - CSS property
 *
 * @example
 * toCssRefProperty("scale", 8) // var(--sp-ref-scale-8)
 * toCssRefProperty("color", "primary") // var(--sp-ref-color-primary)
 */
function toCssRefProperty(category, value) {
  return `var(--sp-ref-${category}-${value})`;
}
